import { useMemo, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import { ZodForm, FormText, FormSwitch } from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import Select from '../../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Type — derived from apps/api/src/routes/conversations/admin/holidays.ts
// + packages/conversations/src/schema/tenant-tables.ts (holiday_configs).
//
// NOTE: GET /admin/conversations/holidays does NOT accept a
// `?businessHoursConfigId=` filter (route returns all rows). Therefore the
// mandatory Business-Hours-config filter is applied CLIENT-SIDE: fetch all
// holidays once, then filter the list by the selected config id. The list is
// hidden until a config is chosen.
// ---------------------------------------------------------------------------

export interface Holiday {
  id: string
  businessHoursConfigId: string
  date: string
  name: string
  allDay: boolean
  partialOpen: string | null
}

interface BhConfig {
  id: string
  name: string
}

const HolidaySchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
    allDay: z.boolean(),
    partialOpen: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm-HH:mm')
      .optional()
      .or(z.literal('')),
  })
  .refine((v) => v.allDay || (v.partialOpen !== undefined && v.partialOpen !== ''), {
    message: 'Partial-open holidays need a time range',
    path: ['partialOpen'],
  })

type HolidayFormValues = z.infer<typeof HolidaySchema>

function PartialOpenField(): JSX.Element | null {
  const { watch } = useFormContext<HolidayFormValues>()
  const allDay = watch('allDay')
  if (allDay) return null
  return <FormText name="partialOpen" label="Partial Open (HH:mm-HH:mm)" placeholder="09:00-13:00" />
}

export default function HolidaysPage(): JSX.Element {
  const { items, loading, create, update, remove } = useAdminResource<Holiday>(
    '/admin/conversations/holidays',
    { responseKey: 'rows' },
  )
  const { items: bhConfigs } = useAdminResource<BhConfig>(
    '/admin/conversations/business-hours',
    { responseKey: 'rows' },
  )

  const [selectedBh, setSelectedBh] = useState<string>('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Holiday | null>(null)
  const [copySource, setCopySource] = useState<Holiday | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const filtered = useMemo(
    () => (selectedBh ? items.filter((h) => h.businessHoursConfigId === selectedBh) : []),
    [items, selectedBh],
  )

  function openCreate(): void {
    setEditing(null)
    setCopySource(null)
    setDrawerOpen(true)
  }

  function openEdit(row: Holiday): void {
    setEditing(row)
    setCopySource(null)
    setDrawerOpen(true)
  }

  function openCopy(row: Holiday): void {
    setEditing(null)
    setCopySource(row)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
    setCopySource(null)
  }

  async function handleSubmit(data: HolidayFormValues): Promise<void> {
    const payload = {
      businessHoursConfigId: selectedBh,
      name: data.name.trim(),
      date: data.date,
      allDay: data.allDay,
      partialOpen: data.allDay ? null : data.partialOpen || null,
    }
    if (editing) await update(editing.id, payload)
    else await create(payload)
    closeDrawer()
  }

  function handleDelete(row: Holiday): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<Holiday>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'Date', accessor: 'date' },
    { label: 'All Day', accessor: (r) => (r.allDay ? 'Yes' : 'No') },
    { label: 'Partial Open', accessor: (r) => r.partialOpen ?? '—' },
  ]

  const defaultValues: HolidayFormValues = editing
    ? {
        name: editing.name,
        date: editing.date,
        allDay: editing.allDay,
        partialOpen: editing.partialOpen ?? '',
      }
    : copySource
      ? {
          name: `Copy of ${copySource.name}`,
          date: copySource.date,
          allDay: copySource.allDay,
          partialOpen: copySource.partialOpen ?? '',
        }
      : { name: '', date: '', allDay: true, partialOpen: '' }

  return (
    <div className="p-4">
      <PageHeader
        title="Holidays"
        description="Manage holiday closures per business hours configuration."
        actions={
          selectedBh ? <Button label="+ New Holiday" onClick={openCreate} /> : undefined
        }
      />

      <div className="mb-4 flex flex-col gap-1">
        <label htmlFor="bh-filter" className="text-sm font-medium">
          Business Hours Config
        </label>
                <Select
          ariaLabel={"Business Hours Config"}
          size="sm"
          value={selectedBh}
          onChange={(v) => setSelectedBh(v)}
          className="w-64"
        >
          <option value="">— Select a config —</option>
          {bhConfigs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
                </Select>
      </div>

      {selectedBh ? (
        <ResourceTable<Holiday>
          columns={columns}
          rows={filtered}
          loading={loading}
          actions={['edit', 'copy', 'delete']}
          onEdit={openEdit}
          onCopy={openCopy}
          onDelete={handleDelete}
          empty={{
            title: 'No holidays',
            description: 'Add a holiday closure for this business hours config.',
            action: <Button label="+ New Holiday" onClick={openCreate} />,
          }}
        />
      ) : (
        <p className="text-sm text-gray-500">
          Select a business hours configuration to view its holidays.
        </p>
      )}

      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        title={editing ? `Edit Holiday: ${editing.name}` : copySource ? `Copy Holiday: ${copySource.name}` : 'New Holiday'}
      >
        <ZodForm schema={HolidaySchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
          <FormText name="name" label="Holiday Name" required autoFocus />
          <FormText name="date" label="Date (YYYY-MM-DD)" required placeholder="2026-12-25" />
          <FormSwitch name="allDay" label="All day" />
          <PartialOpenField />
          <button type="submit" className="rounded bg-[#16273F] px-4 py-2 text-white">
            Save
          </button>
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}
